<script>
import {Link, Head, router} from "@inertiajs/vue3";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import FixtureHeader from "@/Components/FixtureHeader.vue";

export default {
    layout: Authenticated,
    components: {
        Head,
        Link,
        Pagination,
        FixtureHeader,
    },
    props: ['activity_logs', 'page_title', 'page_heading', 'fixture', 'currencies', 'fixture_types', 'regions', 'countries', 'trading_details', 'organisations', 'users', 'expense_types', 'transaction_types', 'service_types', 'quotes', 'quote_services', 'vat_rates', 'cards', 'fixtures_lookup', 'filters', 'entity_type_options'],
    data(props) {
        const buildLookup = (items, keyField, valueField) =>
            (items ?? []).reduce((map, item) => {
                map[item[keyField]] = item[valueField];
                return map;
            }, {});

        const currencyLookup = (props.currencies ?? []).reduce((lookup, currency) => {
            lookup[currency.id] = `${currency.name} (${currency.code})`;
            return lookup;
        }, {});

        const userLookup = (props.users ?? []).reduce((map, u) => {
            map[u.id] = `${u.first_name} ${u.last_name}`.trim();
            return map;
        }, {});

        const serviceTypeLookup = buildLookup(props.service_types, 'id', 'name');
        const organisationLookup = buildLookup(props.organisations, 'id', 'name');

        const quoteServiceLookup = (props.quote_services ?? []).reduce((map, service) => {
            const serviceType = serviceTypeLookup[service.service_type_id] ?? null;
            const supplier = organisationLookup[service.organisation_id] ?? null;
            map[service.id] = service.name || [serviceType, supplier].filter(Boolean).join(' - ') || `Service #${service.id}`;
            return map;
        }, {});

        const vatRateLookup = (props.vat_rates ?? []).reduce((map, rate) => {
            map[rate.id] = `${rate.label} (${rate.percentage}%)`;
            return map;
        }, {});

        const cardLookup = buildLookup(props.cards, 'id', 'name');
        const fixtureLookup = (props.fixtures_lookup ?? []).reduce((map, item) => {
            map[item.id] = item.reference ? `${item.reference} - ${item.title}` : item.title;
            return map;
        }, {});

        const isPaginatedLogs = props.activity_logs
            && !Array.isArray(props.activity_logs)
            && Array.isArray(props.activity_logs.data);

        const filters = props.filters ?? {};

        return {
            activity_logs: isPaginatedLogs ? props.activity_logs.data : (props.activity_logs ?? []),
            pagination_links: isPaginatedLogs ? (props.activity_logs.links ?? []) : [],
            activity_logs_total: isPaginatedLogs ? (props.activity_logs.total ?? 0) : (props.activity_logs?.length ?? 0),
            page_title: props.page_title,
            page_heading: props.page_heading,
            fixture: props.fixture,
            expandedLogs: [],
            filterForm: {
                entityType: filters.entity_type ?? '',
                entitySearch: filters.entity_search ?? '',
                userId: filters.user_id ? String(filters.user_id) : '',
                dateFrom: filters.date_from ?? '',
                dateTo: filters.date_to ?? '',
            },
            currencyLookup,
            fixtureTypeLookup: buildLookup(props.fixture_types, 'id', 'name'),
            regionLookup: buildLookup(props.regions, 'id', 'name'),
            countryLookup: buildLookup(props.countries, 'id', 'name'),
            tradingDetailsLookup: buildLookup(props.trading_details, 'id', 'trading_name'),
            organisationLookup: buildLookup(props.organisations, 'id', 'name'),
            userLookup,
            expenseTypeLookup: buildLookup(props.expense_types, 'id', 'name'),
            transactionTypeLookup: buildLookup(props.transaction_types, 'id', 'name'),
            serviceTypeLookup,
            quoteLookup: buildLookup(props.quotes, 'id', 'name'),
            quoteServiceLookup,
            vatRateLookup,
            cardLookup,
            fixtureLookup: {
                ...fixtureLookup,
                [(props.fixture?.id)]: props.fixture?.title,
            },
        }
    },
    computed: {
        userOptions() {
            return Object.entries(this.userLookup)
                .map(([id, name]) => ({
                    id: String(id),
                    name,
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        entityTypeOptions() {
            if (Array.isArray(this.entity_type_options) && this.entity_type_options.length > 0) {
                return this.entity_type_options;
            }

            const labels = this.activity_logs.map((log) => this.getEntityLabel(log)).filter(Boolean);
            return Array.from(new Set(labels)).sort((a, b) => a.localeCompare(b));
        },
        hasActiveFilters() {
            return Boolean(
                this.filterForm.entityType
                || this.normalizedFilterText(this.filterForm.entitySearch)
                || this.filterForm.userId
                || this.filterForm.dateFrom
                || this.filterForm.dateTo
            );
        },
    },
    watch: {
        filters: {
            immediate: true,
            handler(nextFilters) {
                const latest = nextFilters ?? {};
                this.filterForm = {
                    entityType: latest.entity_type ?? '',
                    entitySearch: latest.entity_search ?? '',
                    userId: latest.user_id ? String(latest.user_id) : '',
                    dateFrom: latest.date_from ?? '',
                    dateTo: latest.date_to ?? '',
                };
            },
        },
    },
    methods: {
        normalizedFilterText(value) {
            return String(value ?? '').trim();
        },
        toggleExpanded(index) {
            this.expandedLogs[index] = !this.expandedLogs[index];
        },
        applyFilters() {
            const query = {};
            const entitySearch = this.normalizedFilterText(this.filterForm.entitySearch);

            if (this.filterForm.entityType) query.entity_type = this.filterForm.entityType;
            if (entitySearch) query.entity_search = entitySearch;
            if (this.filterForm.userId) query.user_id = this.filterForm.userId;
            if (this.filterForm.dateFrom) query.date_from = this.filterForm.dateFrom;
            if (this.filterForm.dateTo) query.date_to = this.filterForm.dateTo;

            router.get(window.location.pathname, query, {
                preserveScroll: true,
                replace: true,
            });
        },
        clearFilters() {
            this.filterForm = {
                entityType: '',
                entitySearch: '',
                userId: '',
                dateFrom: '',
                dateTo: '',
            };

            this.applyFilters();
        },
        formatFieldLabel(field) {
            if (!field) return 'Field';
            return field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
        },
        stripHtml(value) {
            if (typeof value !== 'string') return value;
            const div = document.createElement('div');
            div.innerHTML = value;
            return (div.textContent || div.innerText || '').trim();
        },
        formatBoolean(value) {
            if (value === null || value === undefined || value === '') {
                return 'N/A';
            }
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }

            const normalized = String(value).toLowerCase();
            if (['1', 'true', 'yes'].includes(normalized)) {
                return 'Yes';
            }
            if (['0', 'false', 'no'].includes(normalized)) {
                return 'No';
            }

            return value;
        },
        displayValue(value, field = null) {
            if (value === null || value === undefined || value === '') {
                return 'N/A';
            }
            // Resolve known ID fields to labels
            if (['currency_id', 'buy_currency_id', 'sell_currency_id'].includes(field)) {
                return this.currencyLookup[value] ?? this.currencyLookup[Number(value)] ?? value;
            }
            if (field === 'fixture_type_id') {
                return this.fixtureTypeLookup[value] ?? this.fixtureTypeLookup[Number(value)] ?? value;
            }
            if (field === 'region_id') {
                return this.regionLookup[value] ?? this.regionLookup[Number(value)] ?? value;
            }
            if (field === 'country_id') {
                return this.countryLookup[value] ?? this.countryLookup[Number(value)] ?? value;
            }
            if (field === 'trading_details_id') {
                return this.tradingDetailsLookup[value] ?? this.tradingDetailsLookup[Number(value)] ?? value;
            }
            if (field === 'organisation_id' || field === 'client_id') {
                return this.organisationLookup[value] ?? this.organisationLookup[Number(value)] ?? value;
            }
            if (field === 'fixture_id') {
                return this.fixtureLookup[value] ?? this.fixtureLookup[Number(value)] ?? value;
            }
            if (field === 'quote_id') {
                return this.quoteLookup[value] ?? this.quoteLookup[Number(value)] ?? value;
            }
            if (field === 'quote_service_ids') {
                let serviceIds = value;
                if (typeof serviceIds === 'string') {
                    try {
                        serviceIds = JSON.parse(serviceIds);
                    } catch (e) {
                        return value;
                    }
                }

                if (!Array.isArray(serviceIds)) {
                    return value;
                }

                return serviceIds
                    .map((id) => this.quoteServiceLookup[id] ?? this.quoteServiceLookup[Number(id)] ?? `#${id}`)
                    .join(', ');
            }
            if (field === 'expense_type_id') {
                return this.expenseTypeLookup[value] ?? this.expenseTypeLookup[Number(value)] ?? value;
            }
            if (field === 'transaction_type_id') {
                return this.transactionTypeLookup[value] ?? this.transactionTypeLookup[Number(value)] ?? value;
            }
            if (field === 'service_type_id') {
                return this.serviceTypeLookup[value] ?? this.serviceTypeLookup[Number(value)] ?? value;
            }
            if (['buy_vat_rate_id', 'sell_vat_rate_id', 'country_vat_rate_id', 'management_fee_vat_rate_id'].includes(field)) {
                return this.vatRateLookup[value] ?? this.vatRateLookup[Number(value)] ?? value;
            }
            if (field === 'card_id') {
                return this.cardLookup[value] ?? this.cardLookup[Number(value)] ?? value;
            }
            if (field === 'user_id' || field === 'sent_by') {
                return this.userLookup[value] ?? this.userLookup[Number(value)] ?? value;
            }
            if (['disable_email', 'review_required', 'used_for_invoice', 'proforma_invoice', 'is_paid', 'is_final', 'ignore_unit4', 'email_sent', 'bill_back', 'management_fee_includes_vat'].includes(field)) {
                return this.formatBoolean(value);
            }
            // Strip HTML tags from rich-text fields
            const htmlFields = ['description', 'notes', 'decline_reason', 'terms', 'body'];
            if (htmlFields.includes(field) || (typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value))) {
                return this.stripHtml(value);
            }
            return value;
        },
        getChanges(log) {
            const hiddenFields = new Set(['id', 'lead_passenger', 'invoices', 'import_type']);

            if (Array.isArray(log.changes)) {
                return log.changes
                    .filter((change) => !hiddenFields.has(change.field))
                    .map((change) => ({
                        ...change,
                        old_value: this.displayValue(change.old_value, change.field),
                        new_value: this.displayValue(change.new_value, change.field),
                    }));
            }
            const attributes = log?.properties?.attributes ?? {};
            const oldValues = log?.properties?.old ?? {};
            return Object.entries(attributes)
                .filter(([field]) => !hiddenFields.has(field))
                .map(([field, newValue]) => ({
                    field,
                    label: this.formatFieldLabel(field),
                    old_value: this.displayValue(oldValues[field], field),
                    new_value: this.displayValue(newValue, field),
                }));
        },
        getCauser(log) {
            if (log.causer) return log.causer;
            const firstName = log?.user?.first_name ?? '';
            const lastName = log?.user?.last_name ?? '';
            const fullName = `${firstName} ${lastName}`.trim();
            return fullName || 'N/A';
        },
        getInitials(log) {
            const causer = this.getCauser(log);
            if (causer === 'N/A') return '?';
            return causer.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        },
        getEventLabel(log) {
            return log.description || log.event || 'Updated';
        },
        getEntityLabel(log) {
            return log.entity_type || 'Record';
        },
        getEntityTitle(log) {
            const title = log.title || '';
            const idFallbackMatch = title.match(/: #(\d+)$/);

            if (!idFallbackMatch) {
                return title;
            }

            const subjectId = Number(log.subject_id ?? idFallbackMatch[1]);

            if (log.subject_type === 'App\\Models\\QuoteService') {
                const resolved = this.quoteServiceLookup[subjectId] ?? this.quoteServiceLookup[String(subjectId)];
                return resolved ? `Quote Service Line: ${resolved}` : title;
            }

            if (log.subject_type === 'App\\Models\\Quote') {
                const resolved = this.quoteLookup[subjectId] ?? this.quoteLookup[String(subjectId)];
                return resolved ? `Quote: ${resolved}` : title;
            }

            if (log.subject_type === 'App\\Models\\Fixture') {
                const resolved = this.fixtureLookup[subjectId] ?? this.fixtureLookup[String(subjectId)];
                return resolved ? `Fixture: ${resolved}` : title;
            }

            return title;
        },
        formatDate(dateStr) {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        formatTime(dateStr) {
            const d = new Date(dateStr);
            return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        },
    }
}
</script>

<template>
    <Head :title="page_title"/>

    <div class="w-full">
        <FixtureHeader :fixture="fixture" />
    </div>

    <div class="bg-gray-50 min-h-screen px-6 py-8">
        <!-- Page header -->
        <div class="mb-8 flex items-center justify-between">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 tracking-tight">Fixture Changelog</h1>
                <p class="mt-1 text-sm text-gray-500">A chronological record of all changes made to this fixture.</p>
            </div>
            <span class="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                {{ activity_logs?.length ?? 0 }} of {{ activity_logs_total }} entries
            </span>
        </div>

        <form class="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" @submit.prevent="applyFilters">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div>
                    <label for="entityType" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Item Type</label>
                    <select
                        id="entityType"
                        v-model="filterForm.entityType"
                        class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="">All types</option>
                        <option v-for="type in entityTypeOptions" :key="type" :value="type">
                            {{ type }}
                        </option>
                    </select>
                </div>

                <div>
                    <label for="entitySearch" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Entity Name</label>
                    <input
                        id="entitySearch"
                        v-model="filterForm.entitySearch"
                        type="text"
                        placeholder="Search by entity title"
                        @keydown.enter="applyFilters"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label for="userFilter" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">User</label>
                    <select
                        id="userFilter"
                        v-model="filterForm.userId"
                        class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="">All users</option>
                        <option v-for="user in userOptions" :key="user.id" :value="user.id">
                            {{ user.name }}
                        </option>
                    </select>
                </div>

                <div>
                    <label for="dateFrom" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">From Date</label>
                    <input
                        id="dateFrom"
                        v-model="filterForm.dateFrom"
                        type="date"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label for="dateTo" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">To Date</label>
                    <input
                        id="dateTo"
                        v-model="filterForm.dateTo"
                        type="date"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                </div>
            </div>

            <div class="mt-3 flex justify-end gap-2">
                <button
                    type="submit"
                    class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    Apply filters
                </button>
                <button
                    v-if="hasActiveFilters"
                    type="button"
                    class="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    @click="clearFilters"
                >
                    Clear filters
                </button>
            </div>
        </form>

        <!-- Timeline -->
        <div v-if="activity_logs && activity_logs.length > 0" class="relative max-h-[72vh] overflow-y-auto pr-2">
            <!-- Vertical connector line -->
            <div class="absolute left-[2.375rem] top-0 bottom-0 w-px bg-gray-200 pointer-events-none" aria-hidden="true"></div>

            <ul class="space-y-4">
                <li
                    v-for="(log, index) in activity_logs"
                    :key="log.id ?? index"
                    class="relative flex gap-4"
                >
                    <!-- Avatar bubble -->
                    <div class="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 ring-4 ring-gray-50 text-white text-xs font-bold select-none shadow-sm">
                        {{ getInitials(log) }}
                    </div>

                    <!-- Card -->
                    <div class="flex-1 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <!-- Card header -->
                        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                            <div class="flex items-center gap-2">
                                <span class="inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 capitalize ring-1 ring-inset ring-indigo-200/60">
                                    {{ getEventLabel(log) }}
                                </span>
                                <span class="inline-flex items-center rounded-md bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200/60">
                                    {{ getEntityLabel(log) }}
                                </span>
                                <span v-if="getEntityTitle(log)" class="text-xs text-gray-500">{{ getEntityTitle(log) }}</span>
                                <span class="text-xs text-gray-500">by <span class="font-medium text-gray-700">{{ getCauser(log) }}</span></span>
                            </div>
                            <div class="flex items-center gap-1.5 text-xs text-gray-400">
                                <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5" />
                                </svg>
                                <span>{{ formatDate(log.created_at) }}</span>
                                <span class="text-gray-300">·</span>
                                <span>{{ formatTime(log.created_at) }}</span>
                            </div>
                        </div>

                        <!-- Changes table -->
                        <div v-if="getChanges(log).length > 0" class="px-4 py-3">
                            <table class="w-full text-sm">
                                <thead>
                                    <tr class="text-xs font-medium text-gray-400 uppercase tracking-wide">
                                        <th class="text-left pb-2 w-1/3">Field</th>
                                        <th class="text-left pb-2 w-1/3">Previous</th>
                                        <th class="text-left pb-2 w-1/3">Updated To</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-50">
                                    <tr
                                        v-for="(change, idx) in getChanges(log)"
                                        :key="`${change.field}-${idx}`"
                                        v-show="idx < 3 || expandedLogs[index]"
                                    >
                                        <td class="py-2 pr-4 font-medium text-gray-700 align-top text-sm">
                                            {{ change.label || formatFieldLabel(change.field) }}
                                        </td>
                                        <td class="py-2 pr-4 align-top">
                                            <span class="inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-600 font-mono line-through decoration-red-300">
                                                {{ change.old_value ?? 'N/A' }}
                                            </span>
                                        </td>
                                        <td class="py-2 align-top">
                                            <span class="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 font-mono font-medium">
                                                {{ change.new_value ?? 'N/A' }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Show more / less -->
                            <div v-if="getChanges(log).length > 3" class="mt-2 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                                    @click="toggleExpanded(index)"
                                >
                                    <svg v-if="!expandedLogs[index]" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                    <svg v-else class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                    </svg>
                                    {{ expandedLogs[index] ? 'Show less' : `Show ${getChanges(log).length - 3} more field${getChanges(log).length - 3 > 1 ? 's' : ''}` }}
                                </button>
                            </div>
                        </div>

                        <!-- No changes fallback -->
                        <div v-else class="px-4 py-3 text-sm text-gray-400 italic">
                            No field changes recorded.
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <!-- Empty state -->
        <div v-else class="flex flex-col items-center justify-center py-24 text-center">
            <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <p class="text-sm font-medium text-gray-700">No changelog entries found</p>
            <p class="mt-1 text-xs text-gray-400">Try adjusting your filters or check back after new fixture activity.</p>
        </div>

        <div class="mt-6 flex justify-end" v-if="pagination_links.length > 0">
            <Pagination :links="pagination_links" />
        </div>
    </div>
</template>